import React, { useState, useEffect, useRef } from "react";
import Navbar from "../../Components/Navbar";
import Footer from "../../Components/Footer";
import AdForm from "../../Components/AdComponent/AdForm";
import ThemeWrapper from "../../Components/ThemeWrapper";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import {
    Puzzle, FileSpreadsheet, Settings2, Monitor, Cpu, Database,
    Lock, ShieldCheck, ArrowUpRight, Link, Workflow, Activity,
    LayoutGrid, ArrowRight, PhoneCall, Server, Code
} from "lucide-react";

gsap.registerPlugin(ScrollTrigger);

export default function CustomInternalSoftware_New() {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [theme] = useState('tech-blue'); // Enterprise software feel
    const containerRef = useRef(null);

    useEffect(() => {
        const sections = gsap.utils.toArray(".animate-section");

        // Initial state
        gsap.set(".heading-left", { x: -150, opacity: 0 });
        gsap.set(".heading-right", { x: 150, opacity: 0 });
        gsap.set(".content-fade", { y: 50, opacity: 0 });

        sections.forEach((section, i) => {
            // --- HEADING & CONTENT ANIMATIONS ---
            const heading = section.querySelector(".section-heading");
            const content = section.querySelectorAll(".content-fade");

            if (heading) {
                gsap.to(heading, {
                    x: 0,
                    opacity: 1,
                    duration: 1.8,
                    ease: "expo.out",
                    scrollTrigger: {
                        trigger: section,
                        start: "top 75%",
                        toggleActions: "play none none reverse"
                    }
                });
            }

            if (content.length > 0) {
                gsap.to(content, {
                    y: 0,
                    opacity: 1,
                    duration: 1.5,
                    stagger: 0.2,
                    ease: "power3.out",
                    scrollTrigger: {
                        trigger: section,
                        start: "top 80%",
                        toggleActions: "play none none reverse"
                    }
                });
            }

            // --- PARALLAX & BLUR ---
            if (i > 0) {
                gsap.to(sections[i - 1], {
                    filter: "blur(20px)",
                    opacity: 0.3,
                    scale: 0.95,
                    y: -50,
                    scrollTrigger: {
                        trigger: section,
                        start: "top 70%",
                        end: "top 10%",
                        scrub: true
                    }
                });
            }

            gsap.fromTo(section,
                { y: 100 },
                {
                    y: 0,
                    ease: "none",
                    scrollTrigger: {
                        trigger: section,
                        start: "top bottom",
                        end: "top top",
                        scrub: true
                    }
                }
            );
        });

        return () => ScrollTrigger.getAll().forEach(t => t.kill());
    }, []);

    return (
        <ThemeWrapper theme={theme}>
            <div ref={containerRef} className="min-h-screen bg-[var(--bg-color)] font-sans text-[var(--text-color)] selection:bg-[var(--primary-light)] overflow-x-hidden">
                <Navbar />

                <main className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 pt-8 sm:pt-12 pb-16 sm:pb-32">

                    {/* --- HERO SECTION --- */}
                    <section className="animate-section relative overflow-hidden flex items-center justify-center text-center px-4 sm:px-10 shadow-2xl" style={{ minHeight: 'clamp(500px, 75vh, 800px)', borderRadius: 'clamp(24px, 4vw, 48px)' }}>
                        <div
                            className="absolute inset-0 z-0 bg-cover bg-center transition-all duration-1000 scale-[1.02]"
                            style={{ backgroundImage: `url('/images/edd_images_new/custom_software_new/Hero Section (Top Banner).jpg')` }}
                        >
                            <div className="absolute inset-0 bg-gradient-to-b from-[var(--primary-light)]/40 via-transparent to-white/95"></div>
                            <div className="absolute inset-0 bg-black/5"></div>
                        </div>

                        <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-[300px] sm:h-[500px] w-full bg-white/20 blur-[60px] sm:blur-[120px] rounded-full mx-auto opacity-80 pointer-events-none"></div>

                        <div className="max-w-5xl mx-auto relative z-10 py-12 sm:py-20">
                            <div className="absolute inset-0 bg-white/60 blur-[120px] rounded-full scale-125 pointer-events-none opacity-80"></div>

                            <div className="relative z-10 flex flex-col items-center">
                                <div className="flex justify-center mb-6 sm:mb-10 content-fade">
                                    <div className="bg-white/90 backdrop-blur-2xl p-1.5 sm:p-2 rounded-full px-4 sm:px-8 text-[10px] sm:text-xs md:text-sm font-black uppercase tracking-[0.2em] sm:tracking-[0.4em] text-black border border-white shadow-xl sm:shadow-2xl flex items-center gap-2">
                                        <Code size={14} /> CUSTOM INTERNAL SOFTWARE
                                    </div>
                                </div>

                                <h1 className="drop-shadow-[0_2px_8px_rgba(0,0,0,0.2)] section-heading heading-right font-black tracking-tighter mb-4 sm:mb-6 leading-[0.9] drop-shadow-lg text-[#0f172a]" style={{ fontSize: 'clamp(2rem, 5vw + 1rem, 5.5rem)' }}>
                                    Software Built For <br />
                                    <span className="drop-shadow-[0_4px_8px_rgba(255,255,255,1)] text-[var(--primary)]">Your Operations</span>
                                </h1>

                                <p className="content-fade text-base sm:text-xl md:text-2xl text-slate-800 mb-6 sm:mb-10 leading-relaxed max-w-4xl mx-auto font-bold opacity-95 px-4">
                                    Generic tools force you to adapt to them. Custom systems adapt to you—giving your team the exact workflows, logic, and automation they need.
                                </p>

                                <div className="content-fade flex flex-row flex-wrap justify-center items-center gap-4 sm:gap-6">
                                    <button
                                        onClick={() => setIsModalOpen(true)}
                                        className="bg-[#0f172a] text-white px-6 sm:px-12 py-3 sm:py-5 rounded-full font-black text-sm sm:text-lg md:text-xl hover:bg-[var(--primary)] transition-all active:scale-95 shadow-xl scale-100 hover:scale-105 flex items-center gap-2 sm:gap-3 uppercase tracking-tighter"
                                    >
                                        Request Demo <ArrowRight className="w-4 h-4 sm:w-6 sm:h-6" />
                                    </button>
                                    <button
                                        onClick={() => document.getElementById('architecture')?.scrollIntoView({ behavior: 'smooth' })}
                                        className="bg-white/80 backdrop-blur-md text-[#0f172a] border-2 border-[#0f172a]/10 px-6 sm:px-12 py-3 sm:py-5 rounded-full font-black text-sm sm:text-lg md:text-xl hover:bg-[#0f172a] hover:text-white transition-all active:scale-95 shadow-lg uppercase tracking-tighter"
                                    >
                                        Learn More
                                    </button>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* --- WHY CUSTOM? (Problems) --- */}
                    <section className="animate-section grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-24 items-center px-6 sm:px-12" style={{ marginTop: 'clamp(3rem, 8vw, 6rem)' }}>
                        <div className="relative group order-2 md:order-1">
                            <div className="absolute -inset-10 bg-[var(--primary)] opacity-5 rounded-[60px] blur-3xl group-hover:opacity-15 transition-opacity"></div>
                            <h2 className="section-heading heading-left font-black mb-6 sm:mb-10 relative leading-[0.85] tracking-tighter text-[#0f172a]" style={{ fontSize: 'clamp(2rem, 4.5vw + 1rem, 4.5rem)' }}>Why Custom <br /><span className="text-[var(--primary)] drop-shadow-xl">Systems Exist?</span></h2>

                            <div className="grid grid-cols-1 gap-4 sm:gap-6 content-fade">
                                {[
                                    {
                                        title: "No More Gaps",
                                        desc: "Off the shelf tools never match perfectly. Operational gaps remain.",
                                        icon: Puzzle,
                                        detail: "Fit your workflow 100%."
                                    },
                                    {
                                        title: "Your Logic, Your Rules",
                                        desc: "Your system  →  Your processes  →  Your logic.",
                                        icon: Workflow,
                                        detail: "Built for you."
                                    },
                                    {
                                        title: "Own Your Infrastructure",
                                        desc: "Stop trying to glue 10 different SaaS tools together.",
                                        icon: Lock,
                                        detail: "Total control."
                                    }
                                ].map((item, idx) => (
                                    <div key={idx} className="group/item relative bg-white p-6 sm:p-8 rounded-[32px] border-2 border-slate-50 shadow-sm hover:shadow-xl hover:border-[var(--primary-light)]/20 transition-all duration-500">
                                        <div className="flex items-start gap-5 sm:gap-6">
                                            <div className="w-12 h-12 sm:w-16 sm:h-16 bg-[var(--primary)]/5 rounded-2xl flex items-center justify-center text-[var(--primary)] group-hover/item:scale-110 group-hover/item:rotate-6 transition-transform">
                                                <item.icon size={32} />
                                            </div>
                                            <div className="flex-1">
                                                <h3 className="text-xl sm:text-2xl font-black text-[#0f172a] mb-2 tracking-tighter transition-colors group-hover/item:text-[var(--primary)]">{item.title}</h3>
                                                <p className="text-sm sm:text-lg text-slate-500 font-bold leading-tight mb-2">
                                                    {item.desc}
                                                </p>
                                                <span className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-[var(--primary)] opacity-0 group-hover/item:opacity-100 transition-opacity">
                                                    {item.detail}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="content-fade relative h-[500px] sm:h-[600px] lg:h-[700px] rounded-[48px] sm:rounded-[64px] lg:rounded-[80px] overflow-hidden shadow-[0_60px_120px_-20px_rgba(0,0,0,0.3)] group border-8 border-white order-1 md:order-2">
                            <div
                                className="absolute inset-0 bg-cover bg-center transition-transform duration-[4s] group-hover:scale-110"

                                style={{ backgroundImage: `url('/images/edd_images_new/custom_software_new/Why Custom (Side Image).jpg')` }}
                            ></div>
                            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
                        </div>
                    </section>

                    {/* --- WHAT THIS REPLACES --- */}
                    <section className="animate-section px-6 sm:px-12 relative overflow-hidden" style={{ marginTop: 'clamp(3rem, 10vw, 6rem)' }}>
                        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full bg-[radial-gradient(circle_at_center,var(--primary-light)/5,transparent_70%)] pointer-events-none"></div>

                        <div className="text-center mb-12 sm:mb-20 relative z-10">
                            <span className="text-[var(--primary)] font-black tracking-[0.3em] uppercase text-[10px] sm:text-xs mb-4 block content-fade">The Upgrade</span>
                            <h2 className="section-heading font-black tracking-tighter text-[#0f172a] mb-6" style={{ fontSize: 'clamp(2.5rem, 6vw, 5rem)' }}>What This <span className="text-[var(--primary)] underline decoration-wavy decoration-2 underline-offset-8">Replaces</span></h2>
                            <p className="content-fade text-slate-500 font-bold text-lg sm:text-2xl max-w-3xl mx-auto leading-relaxed">Stop relying on patchwork solutions. Build a unified engine.</p>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8 content-fade relative z-10">
                            {[
                                { title: "Spreadsheets", icon: FileSpreadsheet, color: "bg-green-50 text-green-600" },
                                { title: "Disconnected SaaS", icon: Link, color: "bg-orange-50 text-orange-500" },
                                { title: "Manual Workarounds", icon: Activity, color: "bg-red-50 text-red-500" },
                                { title: "Operational Chaos", icon: Cpu, color: "bg-blue-50 text-blue-500" }
                            ].map((item, idx) => (
                                <div key={idx} className="bg-white p-8 sm:p-10 rounded-[32px] sm:rounded-[48px] border-2 border-slate-50 shadow-[0_15px_45px_rgba(0,0,0,0.03)] hover:shadow-2xl hover:border-[var(--primary-light)]/20 hover:-translate-y-4 transition-all  text-center group">
                                    <div className={`w-16 h-16 sm:w-20 sm:h-20 ${item.color} rounded-3xl flex items-center justify-center mb-6 mx-auto group-hover:scale-110 group-hover:rotate-12 transition-transform shadow-inner`}>
                                        <item.icon size={32} />
                                    </div>
                                    <span className="font-black text-lg sm:text-xl text-[#0f172a] block leading-tight tracking-tighter">{item.title}</span>
                                    <div className="h-1 w-0 bg-[var(--primary)] mx-auto mt-4 group-hover:w-12 transition-all"></div>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* --- PLATFORM ARCHITECTURE (Dark Section) --- */}
                    <section id="architecture" className="animate-section relative overflow-hidden shadow-2xl bg-[#0f172a] border-4 sm:border-8 border-white group" style={{ marginTop: 'clamp(3rem, 10vw, 6rem)', minHeight: 'clamp(400px, 65vh, 650px)', borderRadius: 'clamp(24px, 5vw, 64px)' }}>
                        <div
                            className="absolute inset-0 bg-cover bg-center transition-transform duration-[5s] group-hover:scale-105"

                            style={{ backgroundImage: `url('/images/edd_images_new/custom_software_new/Platform Architecture (Background).jpg')` }}
                        >
                            <div className="absolute inset-0 bg-gradient-to-r from-[#0f172a] via-[#0f172a]/60 to-transparent lg:to-transparent z-10"></div>
                            <div className="absolute top-1/2 left-[10%] -translate-y-1/2 w-[300px] sm:w-[600px] lg:w-[800px] h-[300px] sm:h-[500px] lg:h-[600px] bg-[var(--primary)]/20 blur-[60px] sm:blur-[120px] rounded-full pointer-events-none"></div>
                        </div>

                        <div className="relative z-20 p-8 sm:p-16 lg:p-24 flex flex-col justify-center min-h-[500px] sm:min-h-[600px] lg:min-h-[700px] max-w-5xl">
                            <span className="text-[var(--primary)] font-black tracking-[0.2em] sm:tracking-[0.4em] mb-4 sm:mb-8 uppercase text-sm sm:text-lg content-fade">System Design</span>
                            <h2 className="section-heading heading-right text-white font-black tracking-tighter mb-4 sm:mb-8 leading-[1] drop-shadow-2xl" style={{ fontSize: 'clamp(2rem, 5vw + 1rem, 5rem)' }}>Platform <br />Architecture</h2>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-10 content-fade">
                                {[
                                    { title: "Intuitive User Interface", icon: Monitor },
                                    { title: "Business Logic Layer", icon: Settings2 },
                                    { title: "Secure Data Infra", icon: Database },
                                    { title: "API Integrations", icon: Link }
                                ].map((item, idx) => (
                                    <div key={idx} className="flex items-center gap-4 sm:gap-6 bg-white/5 backdrop-blur-xl p-4 sm:p-6 rounded-2xl border border-white/10">
                                        <div className="w-10 h-10 sm:w-14 sm:h-14 bg-white/10 rounded-xl flex items-center justify-center text-white">
                                            <item.icon size={28} />
                                        </div>
                                        <span className="font-black text-base sm:text-xl text-white tracking-tight leading-tight">{item.title}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>

                    {/* --- CAPABILITIES --- */}
                    <section className="animate-section grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 lg:gap-10 px-2 sm:px-6" style={{ marginTop: 'clamp(3rem, 10vw, 6rem)' }}>
                        {[
                            { title: "Internal Portals", icon: LayoutGrid, desc: "Dedicated dashboards for staff and operations management.", img: "(Internal Portals)-06.png" },
                            { title: "Process Automation", icon: Workflow, desc: "Automate repetitive tasks and approval flows.", img: "(Process Automation)-07.png" },
                            { title: "Complex Logic", icon: Cpu, desc: "Handle intricate calculations and rule-based execution.", img: "(Complex Logic)-08.png" },
                            { title: "Real-Time Data", icon: Activity, desc: "Live dashboards and instant updates across the system.", img: "(Real-Time Data)-09.png" },
                            { title: "Full Ownership", icon: ShieldCheck, desc: "You own the code, the data, and the roadmap.", img: "(Full Ownership)-10.png" },
                            { title: "Infinite Scaling", icon: ArrowUpRight, desc: "Built to grow with your company without license fees.", img: "(Infinite Scaling)-11.png" }
                        ].map((feature, idx) => (
                            <div key={idx} className="content-fade relative group h-[400px] sm:h-[500px] rounded-[32px] sm:rounded-[48px] overflow-hidden shadow-xl border-4 border-white bg-[#0f172a]">
                                <div
                                    className="absolute inset-0 bg-cover bg-center transition-all duration-[2s] group-hover:scale-110 opacity-40 group-hover:opacity-60"
                                    style={{ backgroundImage: `url('/images/edd_images_new/custom_software_new/Capabilities (6 Grid Cards/${feature.img}')` }}
                                ></div>
                                <div className="absolute inset-0 bg-gradient-to-t from-[#0f172a] via-[#0f172a]/40 to-transparent"></div>
                                <div className="relative z-10 p-8 sm:p-12 h-full flex flex-col justify-end">
                                    <div className="w-12 h-12 sm:w-16 sm:h-16 bg-white/10 backdrop-blur-3xl rounded-2xl flex items-center justify-center mb-6 border border-white/20 shadow-2xl group-hover:scale-110 transition-transform">
                                        <feature.icon className="text-[var(--primary)] w-6 h-6 sm:w-8 sm:h-8" />
                                    </div>
                                    <h3 className="text-2xl sm:text-3xl font-black text-white mb-3 leading-tight tracking-tighter">{feature.title}</h3>
                                    <p className="text-sm sm:text-base text-slate-300 font-bold leading-relaxed opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                                        {feature.desc}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </section>

                    {/* --- USE CASES --- */}
                    <section className="animate-section grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 px-4 sm:px-8 lg:px-12 items-center" style={{ marginTop: 'clamp(4rem, 15vw, 10rem)' }}>
                        <div className="space-y-12 sm:space-y-20 text-center lg:text-left">
                            <div className="flex flex-col items-center lg:items-start group">
                                <div className="content-fade inline-flex items-center gap-3 bg-[var(--primary)]/5 px-6 sm:px-10 py-3 sm:py-4 rounded-full border-2 border-[var(--primary)]/20 shadow-xl mb-8 group-hover:border-[var(--primary)]/40 transition-all duration-500">
                                    <Server className="text-[var(--primary)] w-5 h-5 sm:w-6 sm:h-6 animate-pulse" />
                                    <span className="text-[var(--primary)] uppercase tracking-[0.4em] text-xs sm:text-sm font-black">TAILORED SOLUTIONS</span>
                                </div>
                                <h2 className="section-heading heading-right font-black leading-[0.85] tracking-tighter text-[#0f172a] mb-8" style={{ fontSize: 'clamp(3rem, 7vw, 6.5rem)' }}>Software That <br /><span className="text-[var(--primary)]">Fits You.</span></h2>
                                <p className="content-fade text-lg sm:text-2xl text-slate-600 leading-relaxed font-bold opacity-80 max-w-2xl">From managing complex data to empowering employees, we build exactly what you need.</p>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-8 content-fade">
                                {[
                                    { label: "Operations Platforms", icon: Settings2, desc: "Centralized control" },
                                    { label: "Employee Portals", icon: LayoutGrid, desc: "Self service hubs" },
                                    { label: "Workflow Centers", icon: Workflow, desc: "Automated routing" },
                                    { label: "Data Management", icon: Database, desc: "Secure storage" }
                                ].map((item, idx) => (
                                    <div key={idx} className="group p-6 sm:p-10 bg-white rounded-[40px] border-2 border-slate-50 shadow-[0_10px_40px_rgba(0,0,0,0.02)] hover:shadow-2xl hover:border-[var(--primary-light)]/20 transition-all duration-500 cursor-pointer text-center lg:text-left">
                                        <div className="w-14 h-14 sm:w-20 sm:h-20 bg-slate-50 rounded-[28px] flex items-center justify-center mb-6 group-hover:scale-110 group-hover:-rotate-12 transition-transform shadow-inner border border-white mx-auto lg:mx-0">
                                            <item.icon className="text-[var(--primary)] w-8 h-8 sm:w-10 sm:h-10" />
                                        </div>
                                        <span className="font-black text-xl sm:text-3xl block mb-2 text-[#0f172a] tracking-tighter">{item.label}</span>
                                        <span className="text-xs sm:text-sm text-[#0f172a] font-black uppercase tracking-widest opacity-80">{item.desc}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="content-fade relative group p-4 lg:p-0 max-w-4xl mx-auto lg:max-w-none w-full h-full flex items-center">
                            <div className="absolute -inset-10 bg-[var(--primary)]/10 blur-[100px] rounded-full scale-110 opacity-40 group-hover:opacity-60 transition-opacity pointer-events-none"></div>

                            <div className="relative bg-white rounded-[60px] sm:rounded-[80px] p-8 sm:p-16 md:p-20 shadow-[0_50px_100px_-20px_rgba(0,0,0,0.1)] border-4 border-white overflow-hidden text-center lg:text-left flex flex-col justify-between h-full min-h-[600px] sm:min-h-[800px]">
                                <div className="relative z-10">
                                    <div className="flex justify-center lg:justify-start mb-10">
                                        <div className="w-20 h-2 bg-[var(--primary)] rounded-full mb-8"></div>
                                    </div>
                                    <h3 className="font-black mb-8 leading-[0.85] tracking-tighter text-[#0f172a]" style={{ fontSize: 'clamp(2.5rem, 5vw, 4.5rem)' }}>Your Workflows,<br />Your System.</h3>
                                    <p className="text-lg sm:text-2xl text-slate-600 mb-12 leading-relaxed font-bold opacity-90 max-w-xl mx-auto lg:mx-0">
                                        Let’s show you how custom software transforms execution by giving your workflows their own dedicated home.
                                    </p>
                                </div>

                                <div className="relative rounded-[40px] sm:rounded-[60px] overflow-hidden shadow-2xl h-[250px] sm:h-[450px] border-8 border-slate-50 group/img">
                                    <div
                                        className="absolute inset-0 bg-cover bg-center group-hover/img:scale-110 transition-transform duration-[8s] ease-out"

                                        style={{ backgroundImage: `url('/images/edd_images_new/custom_software_new/Use Cases (Tailored Solutions).jpg')` }}
                                    ></div>
                                    <div className="absolute inset-0 bg-gradient-to-t from-[#0f172a]/20 to-transparent"></div>
                                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover/img:opacity-100 transition-opacity bg-black/20 backdrop-blur-[2px]">
                                        <div className="bg-white text-[#0f172a] px-8 py-4 rounded-full font-black text-lg shadow-2xl transform translate-y-4 group-hover/img:translate-y-0 transition-transform">VIEW CASE STUDIES</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* --- CTA SECTION --- */}
                    <section className="animate-section relative flex items-center justify-center overflow-hidden shadow-2xl mx-1 sm:mx-4 border-4 sm:border-[16px] border-white/10" style={{ marginTop: 'clamp(3rem, 12vw, 8rem)', minHeight: 'clamp(500px, 75vh, 850px)', borderRadius: 'clamp(24px, 6vw, 80px)' }}>
                        <div
                            className="absolute inset-0 bg-cover bg-center transition-all duration-[6s] hover:scale-110 shadow-inner"
                            style={{ backgroundImage: `url('/images/edd_images_new/custom_software_new/CTA Section.jpg')` }}
                        ></div>
                        <div className="absolute inset-0 bg-[#0f172a]/75 backdrop-blur-[2px]"></div>
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] sm:w-[600px] lg:w-[1000px] h-[300px] sm:h-[500px] lg:h-[700px] bg-black/60 blur-[80px] sm:blur-[160px] rounded-full opacity-90"></div>

                        <div className="relative z-10 text-center px-4 sm:px-12 max-w-6xl py-12 sm:py-20 flex flex-col items-center">
                            <div className="absolute inset-0 bg-black/50 blur-[110px] rounded-full scale-125 pointer-events-none opacity-90"></div>

                            <div className="relative z-10">
                                <h2 className="section-heading font-black mb-6 sm:mb-10 text-white leading-[0.9] tracking-tighter drop-shadow-2xl" style={{ fontSize: 'clamp(2rem, 7vw + 1rem, 7rem)' }}>Build What<br /><span className="text-[var(--primary)] ">You Need</span></h2>
                                <p className="content-fade text-lg sm:text-2xl md:text-3xl lg:text-4xl text-slate-200 mb-10 sm:mb-20 max-w-4xl mx-auto font-bold leading-relaxed opacity-95 tracking-tight px-4">
                                    Your workflows deserve their own system. Let's build it.
                                </p>
                                <div className="content-fade flex flex-row flex-wrap justify-center items-center gap-4 sm:gap-6 lg:gap-8">
                                    <button
                                        onClick={() => setIsModalOpen(true)}
                                        className="bg-white text-[#0f172a] px-6 sm:px-12 py-3 sm:py-5 rounded-full font-black text-sm sm:text-lg md:text-xl hover:bg-[var(--primary)] hover:text-white transition-all active:scale-95 shadow-xl scale-100 hover:scale-105 uppercase tracking-tighter"
                                    >
                                        REQUEST DEMO
                                    </button>
                                    <a
                                        href="https://calendly.com/digi-dreamworks/onboarding-call"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="bg-white/5 backdrop-blur-3xl border sm:border-2 border-white/30 px-6 sm:px-12 py-3 sm:py-5 rounded-full font-black text-sm sm:text-lg md:text-xl text-white hover:bg-white/10 transition-all flex items-center justify-center gap-2 sm:gap-3 uppercase tracking-tighter shadow-xl no-underline"
                                    >
                                        TALK TO US <PhoneCall className="w-4 h-4 sm:w-6 sm:h-6" />
                                    </a>
                                </div>
                            </div>
                        </div>

                        {/* Decorative dynamic blurs */}
                        <div className="absolute -top-40 sm:-top-60 -left-40 sm:-left-60 w-[400px] sm:w-[800px] h-[400px] sm:h-[800px] bg-[var(--primary)]/40 blur-[150px] sm:blur-[250px] animate-pulse"></div>
                        <div className="absolute -bottom-40 sm:-bottom-60 -right-40 sm:-right-60 w-[400px] sm:w-[800px] h-[400px] sm:h-[800px] bg-blue-600/30 blur-[150px] sm:blur-[250px]"></div>
                    </section>

                </main>

                <AdForm isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} pageTitle="Custom Internal Software" />
                <Footer />
            </div>
        </ThemeWrapper>
    );
}